import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import { InputField } from "../../Shared/InputField"
import { FaFileExport } from "react-icons/fa"
import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const CashFlow = () => {
    const [data, setData] = useState([])
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [errors, setErrors] = useState({})
    const [title, setTitle] = useState('')
    const gridRef = useRef();
    const [formData, setFormData] = useState({
        fdate: new Date().toISOString().slice(0, 10),
        tdate: new Date().toISOString().slice(0, 10),
    })
    const {fdate, tdate} = formData

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        const payload = {fdate, tdate}
        fetchData(payload)
        setTitle(`Cash Flow From ${fdate} To ${tdate}`)
    }, [])

    const columnDefs = [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: false,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Date",
            field: "date",
            filter: 'agDateColumnFilter',
            filterParams: {
                comparator: (filterDate, cellValue) => {
                  const cellDate = new Date(cellValue);
                  cellDate.setHours(0, 0, 0, 0);
                  filterDate.setHours(0, 0, 0, 0);
        
                  if (cellDate < filterDate) return -1;
                  if (cellDate > filterDate) return 1;
                  return 0;
                },
            },
        },
        {
            headerName: "Account",
            field: "account_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Number",
            field: "account_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "User",
            field: "user",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Remark",
            field: "remark",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Debit",
            field: "debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Credit",
            field: "credit",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
    ]

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/account-transations/cash-flow?fdate=${fdate}&tdate=${tdate}`)
            setData(response?.data)
            setIsLoading(false) 

            const output = response?.data.reduce((data, row) => {
                data.debit += parseFloat(row.debit);
                data.credit += parseFloat(row.credit);
                return data;
            }, {debit: 0.00, credit: 0.00, balance: 0.00});
            setPinnedBottomRowData([
                {
                    debit: parseFloat(output.debit).toFixed(2),
                    credit: parseFloat(output.credit).toFixed(2),
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error.response.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error.response.status === 403){
                toast.error(error.response.data.detail);
                navigate('/');
            }else {
                toast.error(error.response.data)
            }
        }
    }

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
        
    }

    const onSubmit = (e) => {
        e.preventDefault()
        
        const payload = {fdate, tdate}
        fetchData(payload)
        setTitle(`Cash Flow From ${fdate} To ${tdate}`)
        
    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi

    const onGridReady=(params)=>{
        gridApi=params.api
    }
    
    const onExportClick=() => {
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <InputField
                        name="fdate"
                        type="date"
                        required="required"
                        value={fdate}
                        onChange={onChange}
                        error={errors.fdate}
                    />
                </div>
                <div className="col-md-2">
                    <InputField
                        name="tdate"
                        type="date"
                        required="required"
                        value={tdate}
                        onChange={onChange}
                        error={errors.tdate}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-4">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
            <div className="row">
                <div className="col-md-12">
                    <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            suppressMenuHide={true}
                            
                            rowData={data}
                            domLayout={"normal"}
                            rowSelection={"multiple"}
                    
                            animateRows={true}
                        
                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}
                        
                        
                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                </div>
            </div>
        </div>
        </div>
      <br/>
    </>
  )
}

export default CashFlow
